import {
  Component,
  computed,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import {
  pefEndYearForSpan,
  pefStartYearForSpan,
  pefYearSpan,
} from './pef-year-span';

export interface YearRangeApplyEvent {
  startYear: number;
  endYear: number;
}

@Component({
  selector: 'lib-pef-year-range-picker',
  template: `
    <div class="year-range-picker">
      <span class="year-range-label">Periodo</span>
      @if (readonly()) {
        <span class="year-range-readonly">
          {{ startYear() ?? '—' }} – {{ endYear() ?? '—' }}
        </span>
      } @else {
      <div class="year-range-inputs">
        <div class="year-field">
          <label class="year-field-label" for="pef-start-year">Da</label>
          <p-inputnumber
            [autofocus]="false"
            inputId="pef-start-year"
            [ngModel]="pendingStartYear()"
            (ngModelChange)="onStartYearChange($event)"
            [useGrouping]="false"
            [showButtons]="true"
            [min]="minYear()"
            [max]="latestStartYear()"
            buttonLayout="horizontal"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            inputStyleClass="year-input"
          />
        </div>
        <div class="year-field">
          <label class="year-field-label" for="pef-end-year">A</label>
          <p-inputnumber
            [autofocus]="false"
            inputId="pef-end-year"
            [ngModel]="pendingEndYear()"
            (ngModelChange)="onEndYearChange($event)"
            [useGrouping]="false"
            [showButtons]="true"
            [min]="earliestEndYear()"
            [max]="maxYear()"
            buttonLayout="horizontal"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            inputStyleClass="year-input"
          />
        </div>
      </div>
      @if (yearRangeChanged()) {
        @if (yearRangeInvalid()) {
          <p class="px-1 pt-1 text-xs text-red-500">
            Il periodo deve essere tra {{ minYearSpan() }} e
            {{ maxYearSpan() }} anni.
          </p>
        }
        <p-button
          label="Modifica"
          size="small"
          styleClass="w-full mt-2"
          [disabled]="yearRangeInvalid()"
          (onClick)="applyYearRange()"
        />
      }
      }
    </div>
  `,
  styleUrl: './pef-year-range-picker.component.css',
  imports: [FormsModule, ButtonModule, InputNumberModule],
})
export class PefYearRangePickerComponent {
  readonly startYear = input<number | null>(null);
  readonly endYear = input<number | null>(null);
  readonly minYearSpan = input.required<number>();
  readonly maxYearSpan = input.required<number>();
  readonly minYear = input<number>(2000);
  readonly maxYear = input<number>(2100);
  /** PEF generation of the answer; `null` keeps the pre-v3 inclusive counting. */
  readonly pefVersion = input<number | null>(null);
  /** Show the period as text; nothing can be applied. */
  readonly readonly = input(false);

  readonly apply = output<YearRangeApplyEvent>();

  readonly pendingStartYear = linkedSignal(() => this.startYear());
  readonly pendingEndYear = linkedSignal(() => this.endYear());

  readonly yearRangeChanged = computed(
    () =>
      this.pendingStartYear() !== this.startYear() ||
      this.pendingEndYear() !== this.endYear(),
  );

  readonly yearRangeInvalid = computed(() => {
    const start = this.pendingStartYear();
    const end = this.pendingEndYear();
    if (start == null || end == null) return true;
    const yearCount = pefYearSpan(start, end, this.pefVersion());
    return yearCount < this.minYearSpan() || yearCount > this.maxYearSpan();
  });

  /** Latest start year that still leaves room for the minimum span. */
  protected readonly latestStartYear = computed(() =>
    pefStartYearForSpan(
      this.pendingEndYear() ?? this.maxYear(),
      this.minYearSpan(),
      this.pefVersion(),
    ),
  );

  /** Earliest end year that still covers the minimum span. */
  protected readonly earliestEndYear = computed(() =>
    pefEndYearForSpan(
      this.pendingStartYear() ?? this.minYear(),
      this.minYearSpan(),
      this.pefVersion(),
    ),
  );

  /**
   * Both handlers only record what the user typed — they deliberately do not
   * move the *other* year to keep the minimum span.
   *
   * `p-inputNumber` fires `ngModelChange` on every keystroke, so retyping a
   * year hands us its prefixes first: "2028" arrives as 2, 20, 202, 2028. A
   * handler that adjusted the other year would act on the meaningless `2` —
   * snapping the start year to `2 - minYearSpan + 1` — and nothing in the
   * later keystrokes would put it back.
   *
   * Keeping the period long enough is instead the job of the `[min]`/`[max]`
   * bindings (see {@link earliestEndYear} / {@link latestStartYear}), which
   * `p-inputNumber` enforces on every committed value — spin, blur and Enter —
   * without ever rewriting the field the user did not touch. A half-typed year
   * shows as invalid until the field is committed, which is what
   * {@link yearRangeInvalid} is for.
   */
  onStartYearChange(year: number): void {
    if (year == null) return;
    this.pendingStartYear.set(year);
  }

  onEndYearChange(year: number): void {
    if (year == null) return;
    this.pendingEndYear.set(year);
  }

  applyYearRange(): void {
    const start = this.pendingStartYear();
    const end = this.pendingEndYear();
    if (start == null || end == null || this.yearRangeInvalid()) return;
    this.apply.emit({ startYear: start, endYear: end });
  }
}
